import { computed, inject, provide, ref, shallowRef, type InjectionKey } from 'vue';
import { useConvexMutation, useConvexQuery } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import type { Doc, Id } from '../../convex/_generated/dataModel';
import { toLogicalEventPoint, toLogicalRect } from '@/utils/screenRotation';
import type { Note } from '@/types';

// Mirrors convex/noteAnnotations.ts's ANNOTATION_CHARS — kept as a separate
// client-side constant rather than imported, since convex/ isn't meant to be
// pulled into the client bundle.
export const ANNOTATION_CHARS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'e', 'n', 'a', 'R', 'L', 'A', 'B', 'C', 'D'] as const;
export type AnnotationChar = typeof ANNOTATION_CHARS[number];

export type NoteAnnotation = Doc<'noteAnnotations'>;

type DragPayload =
  | { kind: 'new'; text: AnnotationChar }
  | { kind: 'existing'; id: Id<'noteAnnotations'>; text: string };

/** What SheetMusicAnnotationsOverlay reports back for the nearest note under the pointer. */
export type NearestNoteResult = { noteIndex: number; x: number; y: number } | null;
type NoteResolver = (pointerScreen: { x: number; y: number }) => NearestNoteResult;

/**
 * Owns annotation data (list/create/move/delete) plus the cross-component drag
 * gesture: a cell dragged out of AnnotationPane, or an existing annotation
 * dragged directly on the sheet, is tracked here so the pane, the sheet
 * overlay, and the floating trash icon (all separate components) can all
 * react to the same in-flight drag without prop-drilling it through GameArea.
 *
 * Provided once per game (GameArea.vue), like useSheetMusic/useGameEngine.
 */
export function useNoteAnnotations(musicalPieceId: Id<'musicalPieces'>) {
  const { data: annotations } = useConvexQuery(
    api.noteAnnotations.listMineForPiece,
    computed(() => ({ musicalPieceId })),
  );

  const byNoteIndex = computed(() => {
    const map = new Map<number, NoteAnnotation>();
    for (const annotation of annotations.value ?? []) map.set(annotation.noteIndex, annotation);
    return map;
  });

  // Optimistic updates so a drop renders the annotation instantly instead of
  // flickering away until the server round-trip resolves (listMineForPiece's
  // cache is patched directly; Convex reconciles/rolls back automatically).
  const { mutate: setAtMutation } = useConvexMutation(api.noteAnnotations.setAt, {
    optimisticUpdate: (localStore, args) => {
      const existing = localStore.getQuery(api.noteAnnotations.listMineForPiece, { musicalPieceId: args.musicalPieceId });
      if (existing === undefined) return;
      const now = Date.now();
      const optimisticAnnotation: NoteAnnotation = {
        _id: crypto.randomUUID() as Id<'noteAnnotations'>,
        _creationTime: now,
        userId: existing[0]?.userId ?? '',
        musicalPieceId: args.musicalPieceId,
        noteIndex: args.noteIndex,
        text: args.text,
        createdAt: now,
        updatedAt: now,
      };
      localStore.setQuery(
        api.noteAnnotations.listMineForPiece,
        { musicalPieceId: args.musicalPieceId },
        [...existing.filter((a) => a.noteIndex !== args.noteIndex), optimisticAnnotation],
      );
    },
  });
  const { mutate: moveToMutation } = useConvexMutation(api.noteAnnotations.moveTo, {
    optimisticUpdate: (localStore, args) => {
      const existing = localStore.getQuery(api.noteAnnotations.listMineForPiece, { musicalPieceId });
      if (existing === undefined) return;
      const moving = existing.find((a) => a._id === args.id);
      if (!moving) return;
      const moved = { ...moving, noteIndex: args.noteIndex, updatedAt: Date.now() };
      localStore.setQuery(
        api.noteAnnotations.listMineForPiece,
        { musicalPieceId },
        [...existing.filter((a) => a._id !== args.id && a.noteIndex !== args.noteIndex), moved],
      );
    },
  });
  const { mutate: removeMutation } = useConvexMutation(api.noteAnnotations.remove, {
    optimisticUpdate: (localStore, args) => {
      const existing = localStore.getQuery(api.noteAnnotations.listMineForPiece, { musicalPieceId });
      if (existing === undefined) return;
      localStore.setQuery(
        api.noteAnnotations.listMineForPiece,
        { musicalPieceId },
        existing.filter((a) => a._id !== args.id),
      );
    },
  });

  // ── Drag gesture ────────────────────────────────────────────────────────
  const dragPayload = shallowRef<DragPayload | null>(null);
  const pointerScreen = ref<{ x: number; y: number } | null>(null);
  const isDragging = computed(() => dragPayload.value !== null);

  // Set by SheetMusicAnnotationsOverlay once mounted — it's the only thing
  // that knows the notes' current on-screen positions.
  let noteResolver: NoteResolver | null = null;
  function registerNoteResolver(fn: NoteResolver | null) {
    noteResolver = fn;
  }

  // Bound by GameArea to the floating trash icon's element.
  const trashEl = shallowRef<HTMLElement | null>(null);

  function isOverTrash(point: { x: number; y: number }): boolean {
    const el = trashEl.value;
    if (!el) return false;
    const rect = toLogicalRect(el.getBoundingClientRect());
    return point.x >= rect.left && point.x <= rect.left + rect.width &&
      point.y >= rect.top && point.y <= rect.top + rect.height;
  }

  // Mirrors isOverTrash into a reactive flag as the pointer moves, purely so
  // the trash icon can react with its own hover-style feedback while it isn't
  // getting a real CSS :hover (the pointer is "captured" by the drag, not
  // actually over the icon in the everyday sense).
  const isPointerOverTrash = ref(false);

  function handlePointerMove(event: PointerEvent) {
    if (!dragPayload.value) return;
    const point = toLogicalEventPoint(event);
    pointerScreen.value = point;
    isPointerOverTrash.value = isOverTrash(point);
  }

  function handlePointerUp(event: PointerEvent) {
    const payload = dragPayload.value;
    if (!payload) return;
    const point = toLogicalEventPoint(event);
    const nearest = noteResolver?.(point) ?? null;

    // Landing on a note within snapping proximity (see
    // findNearestNoteWithinProximity) is the only way a drop actually places
    // or moves an annotation. Anything else — over the trash icon, or simply
    // released with no note in reach (no dashed line was showing) — reads as
    // "let go of this", so it's treated exactly like a trash drop: a 'new'
    // cell vanishes, an 'existing' annotation is deleted.
    if (nearest) {
      if (payload.kind === 'new') setAtMutation({ musicalPieceId, noteIndex: nearest.noteIndex, text: payload.text });
      else moveToMutation({ id: payload.id, noteIndex: nearest.noteIndex });
    } else if (payload.kind === 'existing') {
      removeMutation({ id: payload.id });
    }

    endDrag();
  }

  function endDrag() {
    dragPayload.value = null;
    pointerScreen.value = null;
    isPointerOverTrash.value = false;
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);
    window.removeEventListener('pointercancel', handlePointerUp);
  }

  function beginDrag(payload: DragPayload, event: PointerEvent) {
    dragPayload.value = payload;
    pointerScreen.value = toLogicalEventPoint(event);
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
  }

  function startDragNewCell(text: AnnotationChar, event: PointerEvent) {
    beginDrag({ kind: 'new', text }, event);
  }

  function startDragExisting(annotation: NoteAnnotation, event: PointerEvent) {
    beginDrag({ kind: 'existing', id: annotation._id, text: annotation.text }, event);
  }

  return {
    annotations,
    byNoteIndex,
    setAt: (noteIndex: number, text: AnnotationChar) => setAtMutation({ musicalPieceId, noteIndex, text }),
    remove: (id: Id<'noteAnnotations'>) => removeMutation({ id }),
    dragPayload,
    pointerScreen,
    isDragging,
    isPointerOverTrash,
    trashEl,
    startDragNewCell,
    startDragExisting,
    registerNoteResolver,
  };
}

export type NoteAnnotationsApi = ReturnType<typeof useNoteAnnotations>;

const NOTE_ANNOTATIONS_KEY = Symbol() as InjectionKey<NoteAnnotationsApi>;

export function provideNoteAnnotations(musicalPieceId: Id<'musicalPieces'>) {
  const api = useNoteAnnotations(musicalPieceId);
  provide(NOTE_ANNOTATIONS_KEY, api);
  return api;
}

export function useInjectNoteAnnotations() {
  const injected = inject(NOTE_ANNOTATIONS_KEY);
  if (!injected) throw new Error('useInjectNoteAnnotations must be provided via provideNoteAnnotations');
  return injected;
}

/** Like useInjectNoteAnnotations, but null when no piece is loaded (see GameArea.vue). */
export function useInjectNoteAnnotationsOptional() {
  return inject(NOTE_ANNOTATIONS_KEY, null);
}

/**
 * Finds the closest note (by Euclidean distance in the sheet's local,
 * unscaled coordinate space) to `point`, tie-broken toward the leftmost note.
 * Tremolo repeats are excluded — they share their parent's notehead, so they
 * aren't a distinct visual target to drop on.
 */
export function findNearestNote(notes: Note[], point: { x: number; y: number }): NearestNoteResult {
  let best: { index: number; distSq: number } | null = null;
  notes.forEach((note, index) => {
    if (note.isTremoloRepeat || note.y === undefined) return;
    const dx = note.x - point.x;
    const dy = note.y - point.y;
    const distSq = dx * dx + dy * dy;
    if (
      !best ||
      distSq < best.distSq ||
      (distSq === best.distSq && note.x < notes[best.index].x)
    ) {
      best = { index, distSq };
    }
  });
  if (!best) return null;
  const note = notes[(best as { index: number }).index];
  return { noteIndex: (best as { index: number }).index, x: note.x, y: note.y ?? 0 };
}

// How close (in local, unscaled px) a drag point has to get to a note before
// it counts as "landing" on it — shared by the actual drop (this file) and
// the dashed line/ring preview (SheetMusicAnnotationsOverlay), so a drop that
// wouldn't visually snap never silently attaches either.
export const SNAP_PROXIMITY_PX = 64;

/** Like findNearestNote, but null (no landing spot) once the nearest note is
 *  further than SNAP_PROXIMITY_PX away. */
export function findNearestNoteWithinProximity(notes: Note[], point: { x: number; y: number }): NearestNoteResult {
  const nearest = findNearestNote(notes, point);
  if (!nearest) return null;
  const distance = Math.hypot(nearest.x - point.x, nearest.y - point.y);
  return distance <= SNAP_PROXIMITY_PX ? nearest : null;
}
